import { useState } from 'react'
import Add from '@mui/icons-material/Add'
import BadgeOutlined from '@mui/icons-material/BadgeOutlined'
import {
  Alert,
  Button,
  CircularProgress,
  Divider,
  Stack,
  Typography,
} from '@mui/material'
import useOverlay from '@/hooks/useOverlay'
import { useProjectStartingPoints } from '@/hooks/usePositionStartingPoints'
import { useGetPositions } from '@/service-library/hooks/positions'
import { useGetPositionLookups } from '@/services/hooks/relay/positions'
import { Position } from '@/types/positions'
import { findLookup } from '@/utils/positions'
import { useProjectContext } from '@/components/project-tables/ProjectProvider'
import PositionEditorDialog from './PositionEditorDialog'
import PositionRow from './PositionRow'

function EmptyState() {
  return (
    <Stack
      alignItems="center"
      spacing={0.5}
      sx={{ textAlign: 'center', px: 2, py: 6 }}
    >
      <BadgeOutlined sx={{ fontSize: 32, color: 'text.disabled' }} />
      <Typography variant="body2" sx={{ fontWeight: 600 }}>
        No positions yet
      </Typography>
      <Typography variant="caption" color="text.secondary">
        Add one so work can be directed at whoever holds a job, rather than at a
        person by name.
      </Typography>
    </Stack>
  )
}

export default function PositionList() {
  const { project } = useProjectContext()
  const [editing, setEditing] = useState<Position | 'new' | null>(null)
  const editorOverlay = useOverlay()

  const openEditor = (position: Position | 'new') => {
    setEditing(position)
    editorOverlay.open()
  }

  const { positions, isPending, queryKey } = useGetPositions({
    filters: { limit: '1000', project_id: project.id },
  })

  const {
    positionLookups,
    isPending: lookupsPending,
    isError: lookupsError,
  } = useGetPositionLookups({
    orgCredentialTypeId: project.org_credential_type_id || '',
  })

  // Fetched here, not in each row: every row shares the one result.
  const { startingPointsByInfoTypeId } = useProjectStartingPoints()

  const takenNamesFor = (exceptId?: string) =>
    positions
      .filter((position) => position.id !== exceptId)
      .map(({ name }) => name)

  if (isPending || lookupsPending) {
    return (
      <Stack alignItems="center" sx={{ p: 4 }}>
        <CircularProgress />
      </Stack>
    )
  }

  // The choices offered when setting up a position come from the accounting
  // system, so without one there is nothing to offer.
  const hasIntegration = !!project.org_credential_type_id
  const canAddPosition = hasIntegration && positionLookups.length > 0

  return (
    <Stack spacing={3} sx={{ pb: 6 }}>
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="flex-start"
        spacing={2}
      >
        <Stack spacing={0.5}>
          <Typography variant="h6">Positions</Typography>
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ maxWidth: 560 }}
          >
            A position is your name for a job — &ldquo;Managing Principal&rdquo;
            — paired with the field in your ERP that answers who holds it.
            Holders are read from your ERP every time it is used, so a position
            keeps resolving to the right people when staffing changes.
          </Typography>
        </Stack>
        <Button
          variant="contained"
          startIcon={<Add />}
          disabled={!canAddPosition}
          onClick={() => openEditor('new')}
          sx={{ flexShrink: 0 }}
        >
          Add Position
        </Button>
      </Stack>

      {!hasIntegration && (
        <Alert severity="info">
          This project has no accounting system connected. Positions resolve
          from your ERP, so one has to be connected before they can be set up.
        </Alert>
      )}

      {hasIntegration && lookupsError && (
        <Alert severity="warning">
          Couldn&apos;t reach your accounting system to read what it can
          resolve. Positions can&apos;t be configured until it&apos;s reachable.
        </Alert>
      )}

      {hasIntegration && !lookupsError && positionLookups.length === 0 && (
        <Alert severity="info">
          The accounting system connected to this project doesn&apos;t offer any
          position lookups.
        </Alert>
      )}

      {positions.length === 0 ? (
        <EmptyState />
      ) : (
        <Stack>
          <Divider />
          {positions.map((position) => (
            <PositionRow
              key={position.id}
              position={position}
              lookup={findLookup(positionLookups, position.lookup_code)}
              startingPointLabel={
                position.starting_point_info_type_id
                  ? startingPointsByInfoTypeId.get(
                      position.starting_point_info_type_id,
                    )?.label
                  : undefined
              }
              listQueryKey={queryKey}
              onEdit={() => openEditor(position)}
            />
          ))}
        </Stack>
      )}

      {editing && (
        <PositionEditorDialog
          overlay={editorOverlay}
          position={editing === 'new' ? undefined : editing}
          lookups={positionLookups}
          takenNames={
            editing === 'new' ? takenNamesFor() : takenNamesFor(editing.id)
          }
          listQueryKey={queryKey}
          onExited={() => setEditing(null)}
        />
      )}
    </Stack>
  )
}
